export const HeroSection = () => {
  return (
    <section
      id="home"
      className="relative overflow-hidden max-w-[1440px] mx-auto"
    >
      {/* React hoists this into <head>. A poster referenced only by the video
          element is discovered late; preloading it makes the hero paint on the
          first round trip. Scoped to this component so the other pages, which
          never show it, do not pay for it. */}
      <link
        rel="preload"
        as="image"
        type="image/webp"
        href="/images/keystone-hero-poster-v2.webp"
        fetchPriority="high"
      />

      {/* Text content - above video, with enough top padding to clear fixed header */}
      <div className="flex flex-col items-center text-center px-6 pt-[160px] md:pt-[180px] lg:pt-[200px]">
        <h1 className="text-[32px] md:text-[48px] font-normal text-[#f8f7f4] text-center tracking-[-0.03em] leading-[40px] md:leading-[56px] mb-6">
          The Keystone of Every Successful Shopify Store
        </h1>

        <p className="text-[16px] md:text-[20px] font-normal text-[#c4c4c4] text-center tracking-[-0.002em] leading-[24px] md:leading-[28px] max-w-xl mx-auto">
          AI visibility, custom product options, and loyalty programs built to scale traffic, conversions, and retention.
        </p>
      </div>

      {/* Video */}
      <a
        href="https://apps.shopify.com/partners/key-stone?utm_source=website&utm_medium=organic&utm_campaign=homepage"
        target="_blank"
        rel="noopener noreferrer"
        // Its only child is a decorative video, so the link was focusable with
        // no accessible name at all.
        aria-label="View the Keystone apps on the Shopify App Store"
        // Until the poster decodes, the video box paints nothing, so whatever
        // is behind it shows through. Pinning it to the page colour — which is
        // also what the clip's flat border renders as, see the note on the
        // poster below — means the empty box, the poster and the video are all
        // the same shade and there is no moment where the frame of the clip
        // appears as a rectangle in a different grey.
        className="relative block w-full cursor-pointer aspect-[6/5] bg-[#212121]"
      >
        {/*
          The source clip opens on an empty white tablet and spends its first
          second flying the cards in. Autoplaying it from frame 0 meant every
          page load showed that blank state — which reads as a broken or
          still-loading hero, not as an intro. It is trimmed to start at frame
          28, where the composition has settled; all that remains is the AI SEO
          gauge counting up, and the first painted pixel already looks finished.

          `poster` is frame 0 of the trimmed clip, so the handoff from image to
          video is pixel-identical. (An earlier attempt crossfaded a separate
          <Image> over the video: two near-identical layers at partial opacity
          blend to a washed-out grey for the whole transition. Native `poster`
          has no compositing step and is the correct primitive.)

          Identical *file* pixels are not identical *painted* pixels, though.
          The browser colour-manages a bt709-tagged video on its way to the
          screen and leaves an sRGB image alone, so the clip's flat #1e1e1e
          border paints as #212121 while the same value in the poster paints as
          #1e1e1e. That three-level step across the whole frame was the flash
          at the poster-to-video handoff — visible for one frame, right at the
          start of the clip while the gauge still reads 0. The poster is
          therefore stored pre-lifted by that transform (a gamma of 0.955,
          fitted to the video as Chrome actually paints it) so it lands on
          #212121: the same colour as the page, the box behind it and the
          video. Re-exporting the poster straight out of ffmpeg would undo
          this and bring the flash back.

          No `loop`: the clip ends on the gauge at 73, and restarting would snap
          it back to 0 every three seconds right beside the CTA. It plays once
          and holds on the finished state.

          The original was 3840x3200 with an audio track on a muted element,
          2.9 MB, and took roughly five seconds to start. At the size this box
          actually renders, without audio and without the intro, it is 137 KB.
        */}
        <video
          autoPlay
          muted
          playsInline
          aria-hidden="true"
          preload="auto"
          poster="/images/keystone-hero-poster-v2.webp"
          className="w-full h-full object-cover pointer-events-none"
        >
          <source src="/videos/keystone-hero.webm" type="video/webm" />
          <source src="/videos/keystone-hero.mp4" type="video/mp4" />
        </video>

        {/* Gradient overlay at bottom of video */}
        <div className="absolute bottom-0 left-0 right-0 h-[60%] bg-gradient-to-t from-[#212121] via-[#212121]/80 to-transparent pointer-events-none" />
      </a>
    </section>
  );
};
